import type { GradeResult } from '@shared/grading'
import type { ContentOf, ExerciseResponse, ExerciseType } from '@shared/schemas/exercise'

/**
 * Every renderer takes the same shape, so the player can switch on type
 * without knowing anything about how an individual exercise works.
 *
 * The renderer owns *input*; the player owns grading, feedback chrome, and
 * navigation. That split is why adding a ninth exercise type later means
 * writing one component and one grader, and touching nothing else.
 */
export interface ExerciseRendererProps<T extends ExerciseType> {
  content: ContentOf<T>
  response: Extract<ExerciseResponse, { type: T }> | null
  onChange: (response: Extract<ExerciseResponse, { type: T }>) => void
  /** True once the answer is checked — inputs freeze so feedback stays true. */
  locked: boolean
  result: GradeResult | null
}

/** Whether the current response is complete enough to be checked. */
export function hasAnswer(response: ExerciseResponse | null): boolean {
  if (!response) return false
  switch (response.type) {
    case 'multiple_choice':
      return response.optionId.length > 0
    case 'fill_blank':
      return response.values.some((v) => v.trim().length > 0)
    case 'translation':
      return response.text.trim().length > 0
    case 'word_order':
      return response.order.length > 0
    case 'matching':
      return response.pairs.length > 0
    case 'listening':
      return (response.text?.trim().length ?? 0) > 0 || (response.optionId?.length ?? 0) > 0
    case 'speaking':
      return Boolean(response.mediaId ?? response.transcript)
    case 'conversation':
      return true
  }
}
